import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import RiskBadge from '../RiskBadge';
import { getCaseDetail, updateCase, WHATSAPP_NUMBER } from '../../lib/api';
import { buildWhatsAppSummary, buildGroupedAnswers } from '../../lib/whatsappSummary';
import {
  Copy, MessageCircle, Save, Clock, ArrowRight, UserCog,
  ClipboardList, Stethoscope, AlertTriangle, FileText, BookOpen, History, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = [
  'new_lead',
  'pending_review',
  'pending_assignment',
  'assigned',
  'consultant_accepted',
  'customer_contacted',
  'consultation_scheduled',
  'service_completed',
  'reassigned',
  'closed',
];

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-border/40 last:border-0">
      <span className="text-xs uppercase tracking-wider text-ink-tertiary">{label}</span>
      <span className="text-sm text-ink">{value || '-'}</span>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <h3 className="font-heading text-sm font-semibold text-ink mb-3 flex items-center gap-2">
        <Icon size={14} className="text-terracotta" />
        {title}
      </h3>
      <div className="card-base p-4 sm:p-5">{children}</div>
    </section>
  );
}

export default function CaseDetailDrawer({ caseId, open, onClose, consultants, token, onChanged }) {
  const { lang, t } = useLanguage();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !caseId) return;
    setLoading(true);
    getCaseDetail(caseId, token)
      .then((d) => {
        setDetail(d);
        setNotesDraft(d.case?.admin_notes || '');
      })
      .catch(() => toast.error(lang === 'zh' ? '加载失败' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open, caseId, token, lang]);

  const submission = detail?.submission;
  const cs = detail?.case;
  const recentLogs = detail?.recent_milk_logs || [];
  const linkedMother = detail?.mother || null;
  const motherPreviousSubs = detail?.mother_previous_submissions || [];

  const grouped = useMemo(() => buildGroupedAnswers(submission, lang), [submission, lang]);
  const summary = useMemo(() => buildWhatsAppSummary(submission, lang), [submission, lang]);

  const handleStatus = async (newStatus) => {
    try {
      await updateCase(cs.id, { status: newStatus }, token);
      toast.success(lang === 'zh' ? '状态已更新' : 'Status updated');
      const refreshed = await getCaseDetail(cs.id, token);
      setDetail(refreshed);
      onChanged?.();
    } catch {
      toast.error(lang === 'zh' ? '更新失败' : 'Update failed');
    }
  };

  const handleAssign = async (consultantId) => {
    try {
      await updateCase(cs.id, { assigned_consultant_id: consultantId || '' }, token);
      toast.success(lang === 'zh' ? '已更新' : 'Updated');
      const refreshed = await getCaseDetail(cs.id, token);
      setDetail(refreshed);
      onChanged?.();
    } catch {
      toast.error(lang === 'zh' ? '更新失败' : 'Update failed');
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateCase(cs.id, { admin_notes: notesDraft }, token);
      toast.success(lang === 'zh' ? '备注已保存' : 'Notes saved');
      const refreshed = await getCaseDetail(cs.id, token);
      setDetail(refreshed);
      onChanged?.();
    } catch {
      toast.error(lang === 'zh' ? '保存失败' : 'Save failed');
    } finally {
      setSavingNotes(false);
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success(t('whatsapp_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  const consultantName = (id) => {
    if (!id) return t('admin_unassigned_short');
    const c = consultants.find((x) => x.id === id);
    return c ? c.name : id;
  };

  const renderHistoryEntry = (h, i) => {
    const dt = new Date(h.at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY');
    let label = '';
    let detailText = '';
    if (h.type === 'created') {
      label = t('admin_history_created');
      detailText = `${t(`status_${h.status}`)}${h.consultant_id ? ` · ${consultantName(h.consultant_id)}` : ''}`;
    } else if (h.type === 'status_change') {
      label = t('admin_history_status');
      detailText = `${t(`status_${h.from}`)} → ${t(`status_${h.to}`)}`;
    } else if (h.type === 'assignment') {
      label = t('admin_history_assignment');
      detailText = `→ ${consultantName(h.to)}`;
    } else if (h.type === 'reassignment') {
      label = t('admin_history_reassignment');
      detailText = `${consultantName(h.from)} → ${consultantName(h.to)}`;
    }
    return (
      <div key={i} data-testid={`history-${i}`} className="flex gap-3 py-2 border-b border-border/40 last:border-0">
        <div className="w-7 h-7 rounded-full bg-sage-light flex items-center justify-center flex-shrink-0 mt-0.5">
          <History size={12} className="text-sage" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">{label}</p>
          <p className="text-xs text-ink-secondary">{detailText}</p>
          <p className="text-[10px] text-ink-tertiary mt-0.5">{dt}</p>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-ivory"
        data-testid="case-detail-drawer"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="font-heading text-2xl text-ink flex items-center gap-2">
            <ClipboardList size={20} className="text-terracotta" />
            {t('admin_case_detail')}
          </SheetTitle>
        </SheetHeader>

        {loading || !cs || !submission ? (
          <p className="text-ink-tertiary py-10 text-center">{t('loading')}</p>
        ) : (
          <>
            {/* Top summary */}
            <div className="card-base p-4 mb-6">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <RiskBadge level={submission.risk_level || 'green'} />
                <span className="text-xs uppercase tracking-wider text-ink-tertiary">
                  {submission.language?.toUpperCase()}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-sage-light text-sage font-semibold">
                  {t(`status_${cs.status}`)}
                </span>
              </div>
              <h2 className="font-heading text-lg font-semibold text-ink mb-1">
                {grouped.profile.find((f) => f.label.includes('Main') || f.label.includes('主要'))?.value || '-'}
              </h2>
              <p className="text-xs text-ink-tertiary">
                <Clock size={10} className="inline mr-1" />
                {new Date(submission.created_at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY')}
              </p>
            </div>

            {/* Mother profile section (only when linked) */}
            {linkedMother && (
              <div className="card-base p-4 mb-6 bg-terracotta-light/20" data-testid="drawer-mother-section">
                <div className="text-xs uppercase tracking-wider text-ink-tertiary mb-2">
                  {lang === 'zh' ? '妈妈档案' : 'Linked Mother Profile'}
                </div>
                <div className="font-heading font-semibold text-ink" data-testid="drawer-mother-name">{linkedMother.name}</div>
                <div className="text-sm text-ink-secondary" data-testid="drawer-mother-phone">{linkedMother.phone}</div>
                <div className="text-xs text-ink-tertiary mt-1">
                  {lang === 'zh' ? '宝宝生日' : 'Baby DOB'}: {linkedMother.baby_birth_date} · id: {linkedMother.id.slice(0, 8)}…
                </div>
                {motherPreviousSubs.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/40">
                    <div className="text-xs uppercase tracking-wider text-ink-tertiary mb-2">
                      {lang === 'zh' ? `同一位妈妈的其他 self-check (${motherPreviousSubs.length})` : `Other self-checks by this mother (${motherPreviousSubs.length})`}
                    </div>
                    <div className="space-y-1.5">
                      {motherPreviousSubs.slice(0, 5).map((ps) => (
                        <div key={ps.id} className="flex items-center gap-2 text-xs" data-testid={`drawer-prev-sub-${ps.id}`}>
                          <RiskBadge level={ps.risk_level || 'green'} />
                          <span className="text-ink-tertiary">{new Date(ps.created_at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY')}</span>
                          <span className="text-ink truncate">{ps.main_concern || '-'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick actions */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                  {t('admin_assign')}
                </label>
                <select
                  value={cs.assigned_consultant_id || ''}
                  onChange={(e) => handleAssign(e.target.value)}
                  data-testid="drawer-assign"
                  className="input-base"
                >
                  <option value="">— {t('admin_unassigned')} —</option>
                  {consultants.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                  {t('admin_status')}
                </label>
                <select
                  value={cs.status}
                  onChange={(e) => handleStatus(e.target.value)}
                  data-testid="drawer-status"
                  className="input-base"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`status_${s}`)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* WhatsApp actions */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <button onClick={copySummary} data-testid="drawer-copy-summary" className="btn-outline">
                {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                {copied ? t('whatsapp_copied') : t('admin_copy_summary')}
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(summary)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="drawer-open-whatsapp"
                className="btn-primary"
              >
                <MessageCircle size={16} className="mr-2" />
                {t('admin_open_whatsapp')}
              </a>
            </div>

            {/* Result summary fields */}
            <Section icon={Stethoscope} title={t('admin_section_result')} testid="section-result">
              <Field label={t('admin_field_risk')} value={
                <RiskBadge level={submission.risk_level || 'green'} />
              } />
              <Field label={t('admin_field_result_type')} value={submission.result_type || '-'} />
              <Field label={t('admin_field_pref_service')} value={submission.preferred_service || '-'} />
              <Field label={t('admin_field_pref_consultant')} value={submission.preferred_consultant || '-'} />
              <Field label={t('admin_field_created')} value={new Date(submission.created_at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY')} />
              {grouped.module && (
                <Field label={lang === 'zh' ? '初步结果' : 'Initial result'} value={grouped.module.result} />
              )}
            </Section>

            {/* Profile */}
            <Section icon={ClipboardList} title={t('admin_section_profile')} testid="section-profile">
              {grouped.profile.map((f, i) => (
                <Field key={i} label={f.label} value={f.value} />
              ))}
            </Section>

            {/* Red flags */}
            <Section icon={AlertTriangle} title={t('admin_section_redflag')} testid="section-redflag">
              {grouped.redFlags.map((f, i) => (
                <Field key={i} label={f.label} value={f.value} />
              ))}
            </Section>

            {/* Module */}
            {grouped.module && (
              <Section icon={BookOpen} title={`${t('admin_section_module')}: ${grouped.module.title}`} testid="section-module">
                {grouped.module.answers.length === 0 ? (
                  <p className="text-sm text-ink-tertiary">-</p>
                ) : (
                  grouped.module.answers.map((f, i) => (
                    <Field key={i} label={f.label} value={f.value} />
                  ))
                )}
              </Section>
            )}

            {/* WhatsApp summary preview */}
            <Section icon={MessageCircle} title={t('admin_section_whatsapp')} testid="section-whatsapp">
              <pre
                data-testid="drawer-summary-text"
                className="whitespace-pre-wrap font-sans text-xs sm:text-sm text-ink-secondary leading-relaxed"
              >
                {summary}
              </pre>
            </Section>

            {/* History */}
            <Section icon={History} title={t('admin_section_history')} testid="section-history">
              {(cs.history && cs.history.length > 0) ? (
                cs.history.slice().reverse().map((h, i) => renderHistoryEntry(h, i))
              ) : (
                <p className="text-sm text-ink-tertiary">{t('admin_history_empty')}</p>
              )}
            </Section>

            {/* Admin notes */}
            <Section icon={UserCog} title={t('admin_section_notes')} testid="section-notes">
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder={t('admin_notes_placeholder')}
                rows={4}
                data-testid="drawer-notes-input"
                className="input-base min-h-[100px] mb-3"
              />
              <button
                onClick={saveNotes}
                disabled={savingNotes}
                data-testid="drawer-save-notes"
                className="btn-primary disabled:opacity-50"
              >
                <Save size={14} className="mr-2" />
                {t('admin_save_notes')}
              </button>
            </Section>

            {/* Recent milk logs */}
            <Section icon={FileText} title={t('admin_section_milk')} testid="section-milk">
              <p className="text-xs text-ink-tertiary mb-3 italic">{t('admin_milk_disclaimer')}</p>
              {recentLogs.length === 0 ? (
                <p className="text-sm text-ink-tertiary">{t('admin_no_milk')}</p>
              ) : (
                <div className="space-y-2">
                  {recentLogs.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0 text-xs">
                      <span className="text-ink-secondary">
                        {new Date(l.date_time).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY')}
                      </span>
                      <span className="text-ink">
                        {l.method} · <span className="text-sage font-medium">{l.left_ml}</span>
                        <span className="text-ink-tertiary mx-1">/</span>
                        <span className="text-terracotta font-medium">{l.right_ml}</span> ml
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
